/**
 * drama.ts — Schema Zod untuk pipeline Drama sinematik.
 *
 * Pipeline drama berjalan PARALEL dengan pipeline iklan ('ad').
 * Perbedaan utama:
 *   - Input: DramaTreatment (karakter, konflik, arc, setting) — produk opsional
 *   - LLM concept output: arc cerita + karakter, bukan hook marketing
 *   - LLM scene output: DramaDialogue (campuran narrator + dialogue karakter)
 *   - LLM prompt output: 6 tool prompt gaya sinematik + screenplay + character_brief + mood_board
 */

import { z } from 'zod';

// ---------------------------------------------------------------------------
// TREATMENT — Input user untuk memulai pipeline drama
// ---------------------------------------------------------------------------

/**
 * Karakter dalam drama.
 * Setiap karakter punya nama + deskripsi singkat + peran dalam cerita.
 */
export const dramaCharacterSchema = z.object({
  name: z.string().min(1).max(100).describe('Nama karakter'),
  role: z.enum(['protagonist', 'antagonist', 'supporting', 'narrator'])
    .describe('Peran karakter dalam cerita'),
  description: z.string().min(1).max(500)
    .describe('Deskripsi karakter: penampilan, kepribadian, latar belakang singkat'),
});
export type DramaCharacter = z.infer<typeof dramaCharacterSchema>;

/**
 * Treatment drama — input awal dari user, menggantikan preset kreatif di pipeline 'ad'.
 * Produk bersifat opsional: drama bisa murni fiksi atau sebagai brand storytelling.
 */
export const dramaTreatmentSchema = z.object({
  title: z.string().min(1).max(200)
    .describe('Judul drama / working title'),
  genre: z.enum(['thriller', 'romance', 'family', 'inspirational', 'tragedy', 'comedy_drama', 'social'])
    .describe('Genre cerita'),
  synopsis: z.string().min(10).max(1000)
    .describe('Sinopsis singkat cerita — apa yang terjadi, siapa karakternya, apa konfliknya'),
  characters: z.array(dramaCharacterSchema).min(1).max(5)
    .describe('Karakter utama dan pendukung dalam cerita'),
  conflict: z.string().min(5).max(500)
    .describe('Konflik utama yang menggerakkan cerita — internal atau eksternal'),
  storyArc: z.object({
    opening: z.string().min(5).max(300).describe('Setup awal — dunia sebelum konflik'),
    risingAction: z.string().min(5).max(300).describe('Eskalasi — konflik memanas'),
    climax: z.string().min(5).max(300).describe('Puncak — titik balik cerita'),
    resolution: z.string().min(5).max(300).describe('Penyelesaian — dunia setelah konflik'),
  }).describe('Arc cerita empat babak'),
  setting: z.string().min(5).max(300)
    .describe('Setting lokasi dan era: kapan dan di mana cerita berlangsung'),
  theme: z.string().min(5).max(200)
    .describe('Tema utama yang ingin disampaikan (contoh: "keberanian menghadapi kehilangan")'),
  moodTone: z.string().min(5).max(200)
    .describe('Mood dan tone keseluruhan (contoh: "melankolis tapi penuh harapan")'),
});
export type DramaTreatment = z.infer<typeof dramaTreatmentSchema>;

/**
 * Schema untuk form input treatment di UI — sama seperti dramaTreatmentSchema
 * tapi semua field opsional kecuali title dan synopsis (untuk UX progressive).
 */
export const createDramaTreatmentInputSchema = z.object({
  title: z.string().min(1).max(200),
  genre: z.enum(['thriller', 'romance', 'family', 'inspirational', 'tragedy', 'comedy_drama', 'social'])
    .default('inspirational'),
  synopsis: z.string().min(10).max(1000),
  characters: z.array(dramaCharacterSchema).min(1).max(5),
  conflict: z.string().min(5).max(500),
  storyArc: z.object({
    opening: z.string().min(5).max(300),
    risingAction: z.string().min(5).max(300),
    climax: z.string().min(5).max(300),
    resolution: z.string().min(5).max(300),
  }),
  setting: z.string().min(5).max(300),
  theme: z.string().min(5).max(200),
  moodTone: z.string().min(5).max(200),
});
export type CreateDramaTreatmentInput = z.infer<typeof createDramaTreatmentInputSchema>;

// ---------------------------------------------------------------------------
// DRAMA DIALOGUE — Output per scene dari STORYBOARD_DRAFT drama
// ---------------------------------------------------------------------------

/**
 * Satu baris dialogue karakter.
 */
export const dialogueLineSchema = z.object({
  character: z.string().min(1)
    .describe('Nama karakter yang berbicara — harus ada di treatment.characters'),
  line: z.string().min(1)
    .describe('Teks dialogue yang diucapkan karakter ini'),
  action: z.string().optional()
    .describe('Stage direction opsional: apa yang dilakukan karakter saat mengucapkan ini'),
});
export type DialogueLine = z.infer<typeof dialogueLineSchema>;

/**
 * DramaDialogue — campuran narasi omniscient + dialogue karakter per scene.
 * Disimpan di kolom `scenes.drama_dialogue` sebagai JSON.
 */
export const dramaDialogueSchema = z.object({
  narrator: z.string().optional()
    .describe('Narasi omniscient yang diucapkan narrator di scene ini (opsional — bisa kosong kalau scene full dialogue)'),
  dialogue: z.array(dialogueLineSchema).optional()
    .describe('Array baris dialogue antar karakter (opsional — bisa kosong kalau scene full narasi)'),
  stageDirection: z.string().optional()
    .describe('Arahan adegan keseluruhan scene — suasana, blocking, mood yang harus tercermin'),
});
export type DramaDialogue = z.infer<typeof dramaDialogueSchema>;

// ---------------------------------------------------------------------------
// DRAMA EXTRAS — Output tambahan per scene dari PROMPT_EXPORT drama
// ---------------------------------------------------------------------------

/**
 * DramaExtras — output tambahan khusus pipeline drama.
 * Disimpan di kolom `scenes.drama_extras` sebagai JSON.
 * Melengkapi `scenes.formatted_prompts` yang berisi 6 tool prompt visual.
 */
export const dramaExtrasSchema = z.object({
  screenplay: z.string().min(1)
    .describe(
      'Format naskah/screenplay scene ini — format industri film: ' +
      'INT./EXT. LOKASI - WAKTU, aksi karakter, dialogue, beat emosional. ' +
      'Ditulis seperti screenplay profesional siap produksi.',
    ),
  characterBrief: z.string().min(1)
    .describe(
      'Brief karakter yang MUNCUL di scene ini — siapa mereka dalam konteks scene, ' +
      'apa yang mereka rasakan, apa yang mereka inginkan, dan apa yang menghalangi mereka. ' +
      'Berguna sebagai arahan akting untuk talent.',
    ),
  sceneMoodBoard: z.string().min(1)
    .describe(
      'Deskripsi mood board visual scene ini — referensi sinematik: ' +
      'film/shot inspirasi, palet warna, tekstur, pencahayaan, komposisi. ' +
      'Contoh: "Mood seperti Parasite (2019) opening shot — lantai marmer dingin, ' +
      'cahaya abu overcast dari jendela sempit, warna desaturated dengan satu aksen merah"',
    ),
});
export type DramaExtras = z.infer<typeof dramaExtrasSchema>;

// ---------------------------------------------------------------------------
// LLM OUTPUT SCHEMAS — untuk response_format JSON Schema di CF Workers AI
// ---------------------------------------------------------------------------

/**
 * Output LLM generateDramaConcepts — CONCEPT_GENERATION stage drama.
 * Konsep drama berfokus pada arc cerita dan karakter, bukan hook marketing.
 */
export const generateDramaConceptsOutputSchema = z.object({
  concepts: z.array(
    z.object({
      title: z.string().min(1)
        .describe(
          'Judul konsep yang kuat dan imajinatif — mencerminkan inti emosional cerita. ' +
          'Contoh: "Satu Menit Sebelum Berangkat", "Warisan yang Tak Pernah Diucapkan"',
        ),
      hook: z.string().min(1)
        .describe(
          'Kalimat atau momen pembuka yang LANGSUNG menarik penonton masuk ke cerita. ' +
          'Bukan deskripsi — ini adalah ADEGAN PERTAMA yang terjadi. ' +
          'Contoh: "Seorang wanita tua menemukan surat di laci meja yang sudah 20 tahun dikunci."',
        ),
      angle: z.string().min(1)
        .describe(
          'Penjelasan detail sudut kreatif cerita — min 4 kalimat: ' +
          '(1) Inti pesan emosional yang ingin disampaikan, ' +
          '(2) Siapa protagonis dan journey emosional mereka, ' +
          '(3) Bagaimana konflik berkembang dari opening ke climax, ' +
          '(4) Mengapa sudut ini akan resonan kuat dengan penonton.',
        ),
      storyCore: z.object({
        protagonist: z.string().describe('Siapa protagonis dan apa yang mereka inginkan'),
        centralConflict: z.string().describe('Konflik inti yang menggerakkan seluruh cerita'),
        emotionalJourney: z.string().describe('Perjalanan emosi penonton dari awal sampai akhir'),
        thematicStatement: z.string().describe('Pernyataan tema — satu kalimat inti pesan cerita'),
      }).describe('Inti cerita dalam 4 dimensi'),
    }),
  ).min(1).max(3),
});
export type GenerateDramaConceptsOutput = z.infer<typeof generateDramaConceptsOutputSchema>;

/**
 * Output LLM generateDramaStoryboard — STORYBOARD_DRAFT stage drama.
 * Scene drama menghasilkan visualDescription + dramaDialogue (narasi + dialogue campuran).
 */
export const generateDramaStoryboardOutputSchema = z.object({
  scenes: z.array(
    z.object({
      durationSeconds: z.number().positive().max(30)
        .describe('Durasi klip dalam detik. Min 3, max 15. Total semua scene harus mendekati durasi video.'),
      visualDescription: z.string().min(1)
        .describe(
          'Deskripsi visual SINEMATIK seperti arahan sutradara — min 4 kalimat. ' +
          'Wajib: (1) siapa + posisi + aksi spesifik, (2) setting + detail environment, ' +
          '(3) pencahayaan + tekstur mood, (4) tipe shot + gerakan kamera. ' +
          'Tulis seperti slate di naskah film, bukan deskripsi generik.',
        ),
      scenePrompt: z.string().min(1)
        .describe(
          'Prompt teknis untuk AI image/video — min 3 kalimat. ' +
          'Subjek + aksi, setting + pencahayaan konkret, tipe lensa, depth of field, style sinematik.',
        ),
      narrator: z.string().optional()
        .describe(
          'Narasi omniscient yang diucapkan di scene ini — kalau ada. ' +
          'Harus terdengar seperti narasi film pendek/arthouse, bukan iklan. ' +
          'Min 2 kalimat penuh kalau diisi. JANGAN isi kalau scene ini full dialogue.',
        ),
      dialogue: z.array(
        z.object({
          character: z.string().describe('Nama karakter yang berbicara'),
          line: z.string().describe('Teks dialogue yang diucapkan'),
          action: z.string().optional().describe('Stage direction opsional saat berbicara'),
        }),
      ).optional()
        .describe(
          'Baris dialogue antar karakter — kalau ada. ' +
          'JANGAN isi kalau scene ini full narasi omniscient. ' +
          'Setiap baris harus natural, terdengar seperti dialogue film nyata.',
        ),
      stageDirection: z.string().optional()
        .describe('Arahan adegan keseluruhan scene: suasana, blocking, beat emosional penting'),
      captionText: z.string().min(1)
        .describe(
          'Teks ON-SCREEN yang muncul di layar — bukan narasi. ' +
          'Bisa: judul babak ("TIGA TAHUN KEMUDIAN"), quote karakter, atau keterangan lokasi/waktu.',
        ),
    }),
  ).min(1).max(20),
});
export type GenerateDramaStoryboardOutput = z.infer<typeof generateDramaStoryboardOutputSchema>;

/**
 * Output LLM generateDramaFormattedPrompts — PROMPT_EXPORT stage drama.
 * Gabungan 6 tool prompt visual (gaya sinematik) + 3 output tambahan drama per scene.
 */
export const generateDramaFormattedPromptsOutputSchema = z.object({
  scenes: z.array(
    z.object({
      // ── 6 Tool Prompt Visual (gaya sinematik, bukan commercial) ──────────
      midjourney: z.string().min(1)
        .describe(
          'Prompt Midjourney — gaya SINEMATIK film, bukan iklan. ' +
          'Detail: subjek + ekspresi emosi, lokasi + atmosfer, pencahayaan dramatis, ' +
          'tipe shot sinematik, referensi film jika relevan. ' +
          'Diakhiri parameter: --ar X:Y --v 6 --style raw --cref [mood]',
        ),
      sora: z.string().min(1)
        .describe(
          'Prompt Sora — kalimat sinematik (min 2-3 kalimat penuh). ' +
          'Fokus pada GERAKAN EMOSIONAL dan PERUBAHAN yang terjadi. ' +
          'Mulai dengan shot type + gerakan kamera. Sertakan emosi karakter.',
        ),
      kling: z.string().min(1)
        .describe(
          'Prompt Kling — deskripsi aksi REALISTIS sinematik. ' +
          '[subjek + aksi emosional], [durasi]s, [kualitas], [pencahayaan dramatis], [detail sinematik]',
        ),
      runway: z.string().min(1)
        .describe(
          'Prompt Runway Gen-3 — motion + camera + drama atmosphere. ' +
          '2-3 kalimat. Fokus pada gerakan kamera yang mendukung emosi scene.',
        ),
      veo: z.string().min(1)
        .describe(
          'Prompt Google Veo 2 — 3-4 kalimat natural-language sinematik. ' +
          'Opens with temporal... sekuensial... kualitas + color grade dramatis... ' +
          'Akhiri dengan "portrait format" atau "cinematic widescreen".',
        ),
      generic: z.string().min(1)
        .describe(
          'Prompt generik sinematik — tanpa parameter tool-specific. ' +
          'Min 2 kalimat. Detail visual dramatis, gaya film pendek/arthouse.',
        ),
      // ── Output Tambahan Drama ─────────────────────────────────────────────
      screenplay: z.string().min(1)
        .describe(
          'Format screenplay industri scene ini: ' +
          'INT./EXT. LOKASI - WAKTU\\n\\n[AKSI]\\n\\nNAMA KARAKTER\\n(parenthetical)\\nDialogue\\n\\n' +
          'Tulis lengkap dengan semua beat, aksi, dan dialogue. Siap masuk Final Draft.',
        ),
      characterBrief: z.string().min(1)
        .describe(
          'Brief karakter yang muncul di scene — arahan untuk aktor: ' +
          'apa yang karakter INGINKAN di scene ini, apa yang menghalangi, ' +
          'apa yang mereka SEMBUNYIKAN, beat emosional penting. ' +
          'Satu paragraf per karakter.',
        ),
      sceneMoodBoard: z.string().min(1)
        .describe(
          'Mood board visual naratif — referensi sinematik konkret: ' +
          'film/scene inspirasi, palet warna dominan, tekstur, pencahayaan, ' +
          'komposisi frame, music/sound mood yang cocok. ' +
          'Ditulis seperti brief untuk DOP dan production designer.',
        ),
    }),
  ).min(1).max(20)
    .describe('Array output per scene — urutan HARUS sama dengan urutan scene input.'),

  // Social captions untuk drama tetap ada tapi nadanya berbeda (storytelling, bukan marketing)
  socialCaptions: z.object({
    instagram: z.string().min(1)
      .describe(
        'Caption Instagram untuk drama — buka dengan pertanyaan atau kutipan yang menggugah. ' +
        '3-4 paragraf pendek, fokus pada tema emosional bukan fitur produk. ' +
        'Emoji minimal dan elegan. CTA: "Tonton cerita lengkapnya" atau ajak share. ' +
        '10-15 hashtag drama + storytelling + brand di akhir.',
      ),
    tiktok: z.string().min(1)
      .describe(
        'Caption TikTok — hook emosional 1-2 kalimat yang langsung menyentuh. ' +
        'Max 5-7 baris. CTA storytelling + 5-8 hashtag drama/film.',
      ),
    twitter: z.string().min(1)
      .describe(
        'Tweet tentang drama — max 240 karakter. Kutipan atau momen yang thought-provoking. ' +
        'Boleh format thread. Tanpa hashtag berlebihan (max 2).',
      ),
    facebook: z.string().min(1)
      .describe(
        'Caption Facebook — storytelling panjang 3-5 paragraf. ' +
        'Ceritakan konteks emosional drama, mengapa dibuat, pesan yang ingin disampaikan. ' +
        'CTA jelas. 3-5 hashtag.',
      ),
  }),
});
export type GenerateDramaFormattedPromptsOutput = z.infer<typeof generateDramaFormattedPromptsOutputSchema>;

/**
 * DramaFormattedPrompts — subset output per scene yang disimpan di:
 *   - scenes.formatted_prompts: 6 tool prompt visual
 *   - scenes.drama_extras: screenplay + characterBrief + sceneMoodBoard
 */
export const dramaFormattedPromptsSchema = z.object({
  midjourney: z.string().min(1),
  sora: z.string().min(1),
  kling: z.string().min(1),
  runway: z.string().min(1),
  veo: z.string().min(1),
  generic: z.string().min(1),
});
export type DramaFormattedPrompts = z.infer<typeof dramaFormattedPromptsSchema>;
